﻿/*
 * Purpose: Legal Guardian input form component with validation and context-based state sharing.
 * Boundary: Component only; manages UI and hook state; no external fetching.
 * Invariants:
 * - Validity requires all fields valid and age >= 18.
 * - Exposes isValid and form details via context setters.
 * - ASCII only; comments and strings use plain ASCII.
 */

// Imports
import { createContext, useContext, useEffect } from 'react';

import LegalGuardianLayout from './legalguardian/legalguardian-layout';

import { useLegalGuardian } from './legalguardian/legalguardian-hooks';

// Context for sharing legal guardian state upward
export const LegalGuardianContext = createContext();

// Component LegalGuardian:
// Composes legal guardian form and syncs state with parent via context
const LegalGuardian = ({ services }) => {
    // State Management - initialize form and handlers from hook
    const props = useLegalGuardian({ auth: services?.auth });

    // Context - get setter from parent
    const { setGuardianState } = useContext(LegalGuardianContext) || {};

    // Effect: Update parent via context
    useEffect(() => {
        if (setGuardianState) {
            setGuardianState({
                isValid: props.isValid,
                idNumber: props.form.idNumber,
                firstName: props.form.firstName,
                lastName: props.form.lastName,
                cellNumber: props.cellNumber,
                isValidOTP: props.form.isValidOTP,
                tcAccepted: props.form.tcAccepted,
                age: props.age,
                guardianTrackingConsentChoice: props.guardianTrackingConsentChoice,
            });
        }
    }, [
        props.isValid,
        props.form.idNumber,
        props.form.firstName,
        props.form.lastName,
        props.cellNumber,
        props.form.isValidOTP,
        props.form.tcAccepted,
        props.age,
        props.guardianTrackingConsentChoice,
        setGuardianState,
    ]);

    // Rendering - delegate structure to layout
    return <LegalGuardianLayout {...props} />;
};

// Component metadata - support React DevTools naming
LegalGuardian.displayName = 'LegalGuardian';

// Export - default for use within registration flow
export default LegalGuardian;